import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, fetchMyVideoRequests, fetchTheme, fetchThemes, requestVideo } from '../api/client';
import type { ThemeCharacter, ThemeSummary } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { DEFAULT_VIDEO_PROMPT } from './videoPrompt';

/**
 * キャラ 2 人の相性ドラマの動画をリクエストする（診断の入口 `/t/:themeId` の下。doc/VIDEO.md §8）。
 *
 * 動画は 1 本で数百円〜千円以上かかるので、利用者は直接作らない。リクエストを受け、運営が選んで作る。
 * 作れるのはごく一部だと、出す前に必ず伝える。できたら自分のページの「動画」に届く。
 *
 * 1 人目はこの診断のキャラ。2 人目は、ほかの診断（トップに並ぶ公開の診断）のキャラも選べる。
 */
const PROMPT_MAX = 300;

export function VideoRequestForm({ themeId, themeTitle, characters, initialId }: {
  readonly themeId: string;
  readonly themeTitle: string;
  readonly characters: readonly ThemeCharacter[];
  readonly initialId?: string | null;
}) {
  const { user, requestSignIn } = useAuth();
  const first = characters.find((c) => c.id === initialId)?.id ?? characters[0]?.id ?? '';
  const [a, setA] = useState(first);
  const [b, setB] = useState(characters.find((c) => c.id !== first)?.id ?? '');
  // 2 人目の診断（既定はこの診断）。ほかの診断はトップと同じ一覧から
  const [themeB, setThemeB] = useState(themeId);
  const [others, setOthers] = useState<readonly ThemeSummary[]>([]);
  const [charactersB, setCharactersB] = useState<readonly ThemeCharacter[]>(characters);
  const [loadingB, setLoadingB] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void fetchThemes().then((r) => { if (!cancelled) setOthers(r.themes.filter((t) => t.themeId !== themeId)); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [themeId]);
  useEffect(() => {
    if (themeB === themeId) { setCharactersB(characters); return undefined; }
    let cancelled = false;
    setLoadingB(true);
    void fetchTheme(themeB)
      .then((t) => { if (!cancelled) { setCharactersB(t.characters); setB(t.characters[0]?.id ?? ''); } })
      .catch(() => { if (!cancelled) setError('その診断のキャラクターを読み込めませんでした。'); })
      .finally(() => { if (!cancelled) setLoadingB(false); });
    return () => { cancelled = true; };
  }, [themeB, themeId, characters]);
  const sameChar = themeB === themeId && a === b;
  const [prompt, setPrompt] = useState(DEFAULT_VIDEO_PROMPT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  // 30 日に出せる件数はサーバが持つ（USER_VIDEO_REQUEST_MONTHLY_LIMIT）。ログインしていれば読んで出す
  const [monthlyLimit, setMonthlyLimit] = useState<number | null>(null);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void fetchMyVideoRequests().then((r) => { if (!cancelled) setMonthlyLimit(r.monthlyLimit); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [user]);

  if (characters.length < 2) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!user && !(await requestSignIn())) return;
    setBusy(true);
    try {
      await requestVideo({ cast: [{ themeId, characterId: a }, { themeId: themeB, characterId: b }], prompt: prompt.trim() });
      setSent(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'リクエストを送れませんでした。');
    } finally {
      setBusy(false);
    }
  };

  const option = (c: ThemeCharacter) => <option key={c.id} value={c.id}>{c.name}（{c.label}）</option>;

  return (
    <section className="section video-request" id="video-request" aria-labelledby="video-request-title">
      <h2 className="section__title" id="video-request-title">2 人の相性ドラマの動画をリクエストする</h2>
      <p className="section__note">
        キャラクターを 2 人選んでください。2 人目は、ほかの診断のキャラクターでもかまいません。2 人の性格の違いから、30 秒ほどの短いドラマ動画を作ります。見たい場面があれば、下の欄を書き換えてください（そのままならおまかせ）。
      </p>
      <p className="video-request__warn" role="note">
        <strong>動画は 1 本作るのに大きな費用がかかるため、作れるのはリクエストのうちごく一部です。</strong>
        運営が選んで作り、できたら <Link to="/me?tab=videos">自分のページ</Link> の「動画」に届きます（数日かかることがあります）。
        選ばれなかったときもそこでお知らせします。リクエストは 1 人 1 件ずつで、{monthlyLimit === null ? '30 日に出せる件数にも上限があります' : `30 日で ${monthlyLimit} 件までです`}。
      </p>
      {sent ? (
        <p className="section__note" role="status">
          リクエストを受け付けました。結果は <Link to="/me?tab=videos">自分のページ</Link> の「動画」でお知らせします。
        </p>
      ) : (
        <form className="video-request__form" onSubmit={(e) => void submit(e)}>
          <div className="video-request__pair">
            <label className="field">
              <span className="field__label">1 人目（{themeTitle}）</span>
              <select className="field__input" value={a} onChange={(e) => setA(e.target.value)}>{characters.map(option)}</select>
            </label>
            <span className="video-request__x" aria-hidden="true">×</span>
            <div className="video-request__other">
              <label className="field">
                <span className="field__label">2 人目の診断</span>
                <select className="field__input" value={themeB} onChange={(e) => setThemeB(e.target.value)}>
                  <option value={themeId}>この診断（{themeTitle}）</option>
                  {others.map((t) => <option key={t.themeId} value={t.themeId}>{t.title}</option>)}
                </select>
              </label>
              <label className="field">
                <span className="field__label">2 人目</span>
                <select className="field__input" value={b} disabled={loadingB} onChange={(e) => setB(e.target.value)}>{charactersB.map(option)}</select>
              </label>
            </div>
          </div>
          <label className="field">
            <span className="field__label">どんな場面を見たいか（{prompt.trim().length}／{PROMPT_MAX} 字）</span>
            <textarea
              className="field__input" rows={3} maxLength={PROMPT_MAX} value={prompt}
              placeholder="例：2 人で旅行の行き先を決める。片方は計画を立てたい、もう片方は行き当たりばったりで行きたい"
              onChange={(e) => setPrompt(e.target.value)}
            />
          </label>
          {sameChar && <p className="page__error" role="alert">違うキャラクターを 2 人選んでください。</p>}
          {error && <p className="page__error" role="alert">{error}</p>}
          <div className="page__actions">
            <button className="button button--primary" type="submit" disabled={busy || loadingB || !b || sameChar || (prompt.trim().length > 0 && prompt.trim().length < 5)}>
              {busy ? '送っています…' : user ? 'リクエストする' : 'ログインしてリクエストする'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
